import {
  CheckOutlined,
  DownOutlined,
  LoadingOutlined,
  SearchOutlined,
  UpOutlined,
} from '@ant-design/icons';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Checkbox } from 'antd';
import { debounce } from 'lodash-es';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import React, {
  CSSProperties,
  ReactNode,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  useFilterStore,
  useFilterStoreLabelAndValue,
} from 'app/hooks/useFilterStore';
import { cn } from 'lib/utils';

export type MultiSelectPopoverValue = string | number;

export type MultiSelectPopoverOption = {
  value: MultiSelectPopoverValue;
  label: string;
  icon?: ReactNode | string;
  url?: string;
  isInactive?: boolean;
  code?: string;
  priorityLevel1?: boolean;
};

export interface MultiSelectPopoverProps {
  value?: MultiSelectPopoverValue | MultiSelectPopoverValue[] | null;
  onChange?: (
    value: MultiSelectPopoverValue | MultiSelectPopoverValue[] | null,
  ) => void;
  onBlur?: () => void;
  options: MultiSelectPopoverOption[];
  placeholder?: string;
  className?: string;
  isSingleSelect?: boolean;
  allowDeselect?: boolean;
  readOnly?: boolean;
  isCloseAfterSelect?: boolean;
  iconUp?: ReactNode;
  iconDown?: ReactNode;
  maxLength?: number;
  isShowValue?: 0 | 1 | 3;
  formatShowOption?: 0 | 1 | 2;
  iconDropDownSide?: 'left' | 'right';
  classNamePopoverContent?: string;
  classNameBtn?: string;
  errorMessage?: string;
  outputMsg?: string;
  isAlwaysShowText?: boolean;
  optionIsLink?: boolean;
  classNameOverideLink?: string;
  classNameOverideEllipsisText?: string;
  searchLabelAndValue?: boolean;
  isShowRowByRow?: boolean;
  isShowIndex?: boolean;
  isShowSelectAll?: boolean;
  prefixIcon?: ReactNode;
  classNameErrorMessage?: string;
  showErrorWhenReadOnly?: boolean;
  isShowSearch?: boolean;
  name?: string;
  isLoading?: boolean;
  isAlwaysShowSelectedCount?: boolean;
  storeFilterName?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

type EllipsisTextProps = {
  text: string;
  className?: string;
  onClick?: () => void;
};

const sortOptionsBySelection = (
  options: MultiSelectPopoverOption[],
  selectedValues: MultiSelectPopoverValue[],
) => {
  const checkedPriority = options
    .filter(
      option => selectedValues.includes(option.value) && option.priorityLevel1,
    )
    .sort((a, b) => a.label?.localeCompare(b.label));
  const checked = options
    .filter(
      option => selectedValues.includes(option.value) && !option.priorityLevel1,
    )
    .sort((a, b) => a.label?.localeCompare(b.label));
  const uncheckedPriority = options
    .filter(
      option => !selectedValues.includes(option.value) && option.priorityLevel1,
    )
    .sort((a, b) => a.label?.localeCompare(b.label));
  const unchecked = options
    .filter(
      option =>
        !selectedValues.includes(option.value) && !option.priorityLevel1,
    )
    .sort((a, b) => a.label?.localeCompare(b.label));

  return [...checkedPriority, ...checked, ...uncheckedPriority, ...unchecked];
};

export const EllipsisText = ({
  text,
  className,
  onClick,
}: EllipsisTextProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = spanRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <span
      ref={spanRef}
      className={cn(
        'block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap',
        className,
      )}
      title={isTruncated ? text : undefined}
      onClick={onClick}
    >
      {text}
    </span>
  );
};

export const MultiSelectPopover = memo(
  ({
    value,
    onChange,
    onBlur,
    options,
    placeholder,
    className,
    isSingleSelect = false,
    allowDeselect = true,
    readOnly = false,
    isCloseAfterSelect = true,
    iconUp,
    iconDown,
    maxLength = 50,
    isShowValue = 0,
    formatShowOption = 0,
    iconDropDownSide = 'right',
    classNamePopoverContent,
    classNameBtn,
    errorMessage,
    outputMsg,
    isAlwaysShowText,
    optionIsLink,
    classNameOverideLink,
    classNameOverideEllipsisText,
    searchLabelAndValue,
    isShowRowByRow = false,
    isShowIndex,
    isShowSelectAll = false,
    prefixIcon,
    classNameErrorMessage,
    showErrorWhenReadOnly = false,
    isShowSearch = true,
    name,
    isLoading = false,
    isAlwaysShowSelectedCount = true,
    storeFilterName,
    style,
    disabled,
  }: MultiSelectPopoverProps) => {
    const translations = useSelector(selectTranslations);
    const t = (key: string, fallback: string) =>
      translations?.[key] ?? fallback;
    const [open, setOpen] = useState(false);
    const refOpen = useRef(false);
    const prevSorted = useRef<MultiSelectPopoverOption[]>([]);
    const filterStoreKey = storeFilterName || 'default';
    const labelFilterStore = useFilterStore(filterStoreKey, options);
    const labelAndValueFilterStore = useFilterStoreLabelAndValue(
      filterStoreKey,
      options,
    );
    const filterStore = searchLabelAndValue
      ? labelAndValueFilterStore
      : labelFilterStore;
    const { filteredOptions, handleChange, setOptions } = filterStore;

    const selectedValues = useMemo<MultiSelectPopoverValue[]>(() => {
      if (isSingleSelect) {
        return value ? [value as MultiSelectPopoverValue] : [];
      }
      return Array.isArray(value) ? value : [];
    }, [isSingleSelect, value]);

    const selectedOptions = useMemo(
      () => options.filter(option => selectedValues.includes(option.value)),
      [options, selectedValues],
    );

    const getSelectedText = (option: MultiSelectPopoverOption) => {
      if (isShowValue === 1) return option.value.toString();
      if (isShowValue === 3) return option.code || '';
      return option.label;
    };

    const getOptionText = (option: MultiSelectPopoverOption) => {
      if (formatShowOption === 1) return option.value.toString();
      if (formatShowOption === 2) return `${option.label} (${option.value})`;
      return option.label;
    };

    const selectedLabels = selectedOptions.map(getSelectedText).filter(Boolean);
    const joinedLabels = selectedLabels.join(', ');
    const selectedCount = selectedLabels.length;
    const selectedCountText = t(
      'b2c.common.selectedCount_other',
      'Đã chọn {count} mục',
    )
      .replace('{count}', selectedCount.toString())
      .replace('{{count}}', selectedCount.toString());
    const shouldShowSelectedCount = isAlwaysShowSelectedCount
      ? selectedCount === 1
        ? joinedLabels.length >= maxLength
        : selectedCount > 1
      : joinedLabels.length > maxLength;

    const sortedOptions = useMemo(() => {
      if (!refOpen.current) {
        const sorted = sortOptionsBySelection(filteredOptions, selectedValues);
        prevSorted.current = sorted;
        return sorted;
      }

      const nextOptions = prevSorted.current.filter(option =>
        filteredOptions.some(
          filteredOption => filteredOption.value === option.value,
        ),
      );
      const knownValues = new Set(nextOptions.map(option => option.value));
      const newOptions = filteredOptions.filter(
        option => !knownValues.has(option.value),
      );

      return [...nextOptions, ...newOptions];
    }, [filteredOptions, selectedValues]);

    const debouncedSearch = useMemo(
      () => debounce((nextKeyword: string) => handleChange(nextKeyword), 300),
      [handleChange],
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
      setOptions(options);
    }, [options, setOptions]);

    useEffect(() => {
      if (!open) handleChange('');
    }, [handleChange, open]);

    const handleOpenChange = (nextOpen: boolean) => {
      if (readOnly || disabled) return;
      setOpen(nextOpen);
      refOpen.current = nextOpen;
      if (!nextOpen) {
        onBlur?.();
      }
    };

    const handleSelect = (option: MultiSelectPopoverOption) => {
      const isChecked = selectedValues.includes(option.value);
      if (optionIsLink && option.url) {
        window.open(option.url, '_blank', 'noopener,noreferrer');
      }

      if (isSingleSelect) {
        onChange?.(allowDeselect && isChecked ? '' : option.value);
        if (isCloseAfterSelect && !(allowDeselect && isChecked)) {
          handleOpenChange(false);
        }
        return;
      }

      onChange?.(
        isChecked
          ? selectedValues.filter(
              selectedValue => selectedValue !== option.value,
            )
          : [...selectedValues, option.value],
      );
    };

    const filteredOptionValues = filteredOptions.map(option => option.value);
    const isAllSelected =
      filteredOptionValues.length > 0 &&
      filteredOptionValues.every(optionValue =>
        selectedValues.includes(optionValue),
      );
    const selectedOptionCount = filteredOptionValues.filter(optionValue =>
      selectedValues.includes(optionValue),
    ).length;

    const handleSelectAll = () => {
      if (isSingleSelect) return;
      onChange?.(
        isAllSelected
          ? selectedValues.filter(
              selectedValue => !filteredOptionValues.includes(selectedValue),
            )
          : Array.from(new Set([...selectedValues, ...filteredOptionValues])),
      );
    };

    const displayContent = () => {
      if (!selectedValues.length) {
        return (
          <span className="italic text-[#dcdcdc]">
            {placeholder || t('b2c.common.select', 'Chọn')}
          </span>
        );
      }
      if (shouldShowSelectedCount && !isAlwaysShowText) {
        return selectedCountText;
      }
      return selectedOptions.map((option, index) => (
        <React.Fragment key={option.value}>
          {isShowRowByRow && readOnly && index > 0 ? <br /> : null}
          <span
            className={option.isInactive ? 'text-[#8c8c8c]' : 'text-[#111111]'}
          >
            {isShowIndex && readOnly ? `${index + 1}. ` : ''}
            {getSelectedText(option)}
          </span>
          {index < selectedOptions.length - 1 && !isShowRowByRow ? ', ' : ''}
        </React.Fragment>
      ));
    };

    return (
      <div className={cn('w-full', className)} style={style}>
        <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
          <PopoverPrimitive.Trigger asChild>
            <button
              data-field-name={name}
              data-has-value={selectedValues.length ? 'true' : 'false'}
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-[45px] w-full items-center justify-between rounded-lg border border-[#d9d9d9] px-3 text-left font-["Be_Vietnam_Pro",sans-serif] text-sm font-normal outline-none shadow-none transition-[border-color,box-shadow,background] duration-150',
                selectedValues.length ? 'text-[#111111]' : 'text-[#dcdcdc]',
                readOnly
                  ? 'cursor-default bg-[#f5f5f5] hover:border-[#d9d9d9] hover:shadow-none focus:border-[#d9d9d9] focus:shadow-none data-[state=open]:border-[#d9d9d9] data-[state=open]:shadow-none'
                  : 'cursor-pointer bg-white hover:border-[#2997ff] hover:shadow-[0_0_0_2px_rgba(0,122,255,0.2)] focus:border-[#2997ff] focus:shadow-[0_0_0_2px_rgba(0,122,255,0.2)] data-[state=open]:border-[#2997ff] data-[state=open]:shadow-[0_0_0_2px_rgba(0,122,255,0.2)]',
                'disabled:cursor-not-allowed disabled:border-[#d9d9d9] disabled:bg-[#f5f5f5] disabled:text-[rgba(0,0,0,0.25)]',
                classNameBtn,
              )}
            >
              {iconDropDownSide === 'left' && !readOnly ? (
                <span className="mr-2 inline-flex items-center text-xs text-[rgba(0,0,0,0.45)]">
                  {open
                    ? iconUp || <UpOutlined />
                    : iconDown || <DownOutlined />}
                </span>
              ) : null}
              {prefixIcon ? (
                <span className="mr-2 inline-flex items-center">
                  {prefixIcon}
                </span>
              ) : null}
              <span
                className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                title={isAlwaysShowText ? joinedLabels : undefined}
              >
                {displayContent()}
              </span>
              {iconDropDownSide === 'right' && !readOnly ? (
                <span className="ml-2 inline-flex items-center text-xs text-[rgba(0,0,0,0.45)]">
                  {open
                    ? iconUp || <UpOutlined />
                    : iconDown || <DownOutlined />}
                </span>
              ) : null}
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              collisionPadding={16}
              className={cn(
                'z-[1050] flex max-h-[min(384px,var(--radix-popover-content-available-height))] w-80 max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border border-[#ededed] bg-white shadow-[0_12px_36px_rgba(15,23,42,0.14)] outline-none data-[state=closed]:animate-[multiSelectPopoverOut_0.1s_ease-in] data-[state=open]:animate-[multiSelectPopoverIn_0.14s_ease-out]',
                classNamePopoverContent,
              )}
            >
              {!isLoading && isShowSearch && options.length >= 5 ? (
                <div className="relative border-b border-[#ededed] bg-[#fafafa] p-2">
                  <SearchOutlined className="absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-[#bfbfbf]" />
                  <input
                    type="search"
                    className="h-[38px] w-full rounded-lg border border-[#ededed] bg-white py-0 pl-[34px] pr-3 text-sm text-[#111111] outline-none transition-[border-color,box-shadow] duration-150 placeholder:italic placeholder:text-[#dcdcdc] hover:border-[#2997ff] hover:shadow-[0_0_0_2px_rgba(0,122,255,0.12)] focus:border-[#2997ff] focus:shadow-[0_0_0_2px_rgba(0,122,255,0.12)]"
                    placeholder={t('b2c.txt.search', 'Tìm kiếm')}
                    onChange={event =>
                      debouncedSearch(event.target.value.trim())
                    }
                  />
                </div>
              ) : null}

              {!isLoading && isShowSelectAll && !isSingleSelect ? (
                <div
                  className="mx-2 mt-2 flex h-10 cursor-pointer items-center rounded-lg border border-[#ededed] px-2.5 text-sm leading-none text-[#111111] hover:bg-[#fafafa]"
                  onClick={handleSelectAll}
                >
                  <span className="mr-2.5 inline-flex h-full w-5 flex-shrink-0 items-center justify-center [&_.ant-checkbox-inner]:!h-5 [&_.ant-checkbox-inner]:!w-5 [&_.ant-checkbox-wrapper]:!m-0 [&_.ant-checkbox-wrapper]:!inline-flex [&_.ant-checkbox-wrapper]:!items-center [&_.ant-checkbox-wrapper]:!leading-none [&_.ant-checkbox]:!top-0 [&_.ant-checkbox]:!inline-flex [&_.ant-checkbox]:!items-center">
                    <Checkbox checked={isAllSelected} />
                  </span>
                  <span>{t('b2c.txt.selectAll', 'Chọn tất cả')}</span>
                  <span className="ml-auto flex h-full flex-shrink-0 items-center text-xs leading-none text-[#8c8c8c]">
                    {t('b2c.common.selectedOfTotal', '{selected}/{total} đã chọn')
                      .replace('{selected}', selectedOptionCount.toString())
                      .replace('{total}', filteredOptions.length.toString())}
                  </span>
                </div>
              ) : null}

              <div className="max-h-[264px] overflow-y-auto p-1.5">
                {isLoading ? (
                  <div className="flex min-h-[42px] items-center justify-center gap-2 text-sm text-[#8c8c8c]">
                    <LoadingOutlined />
                    <span>{t('b2b.txt.loadingData', 'Đang tải')}</span>
                  </div>
                ) : sortedOptions.length ? (
                  sortedOptions.map(option => {
                    const isChecked = selectedValues.includes(option.value);
                    return (
                      <div
                        key={`${option.label}-${option.value}`}
                        className={cn(
                          'flex min-h-[38px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 transition-colors duration-150',
                          option.isInactive
                            ? 'text-[#8c8c8c]'
                            : 'text-[#111111]',
                          isChecked
                            ? 'bg-[#fff7e6] hover:bg-[#fff1d6]'
                            : 'bg-white hover:bg-[#fafafa]',
                        )}
                        onClick={() => handleSelect(option)}
                      >
                        {option.icon ? (
                          <span className="inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain">
                            {typeof option.icon === 'string' ? (
                              <img src={option.icon} alt="" />
                            ) : (
                              option.icon
                            )}
                          </span>
                        ) : null}
                        <EllipsisText
                          text={getOptionText(option)}
                          className={classNameOverideEllipsisText}
                        />
                        <span className="ml-auto inline-flex w-5 items-center justify-center text-[#f8b117]">
                          {isChecked ? (
                            <CheckOutlined className={classNameOverideLink} />
                          ) : null}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex min-h-[42px] items-center justify-center gap-2 text-sm text-[#8c8c8c]">
                    {outputMsg || t('b2b.txt.haveNoData', 'Không có dữ liệu')}
                  </div>
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>

        {errorMessage && (!readOnly || showErrorWhenReadOnly) ? (
          <p
            className={cn(
              'mt-1 text-[13px] text-[var(--color-10)]',
              classNameErrorMessage,
            )}
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  },
);
